import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { cn, TooltipProvider } from '@safe-minds-ai/ui';
import { Sidebar, SidebarContext } from '../sidebar';
import { Topbar } from './topbar';

type DashboardLayoutProps = {
  children: React.ReactNode;
};

export function DashboardLayout({ children }: DashboardLayoutProps) {
  const location = useLocation();
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  useEffect(() => {
    setIsMobileOpen(false);
  }, [location.pathname]);

  const toggleMobile = () => setIsMobileOpen((curr) => !curr);
  const closeMobile = () => setIsMobileOpen(false);

  return (
    <TooltipProvider>
      <div className="min-h-screen bg-[radial-gradient(circle_at_top,_#eef3ff,_#f6f8ff_45%,_#e7eefc_100%)] dark:bg-[radial-gradient(circle_at_top,_#0b1222,_#0b1222_45%,_#05070f_100%)]">
        <div className="pointer-events-none fixed inset-0 z-0">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,_rgba(90,140,255,0.18),_transparent_40%)] dark:bg-[radial-gradient(circle_at_20%_20%,_rgba(76,120,255,0.12),_transparent_50%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_30%,_rgba(90,140,255,0.12),_transparent_38%)] dark:bg-[radial-gradient(circle_at_80%_30%,_rgba(76,120,255,0.08),_transparent_50%)]" />
          <div className="absolute inset-0 opacity-30 [background-image:radial-gradient(circle,_rgba(148,170,220,0.35)_1px,_transparent_1px)] [background-size:18px_18px] dark:opacity-12" />
        </div>
        <div className="relative z-10 flex h-screen">
          <SidebarContext.Provider value={false}>
            <div
              className={cn(
                'fixed inset-y-0 left-0 z-40 -translate-x-full transition-transform duration-300 lg:translate-x-0',
                isMobileOpen && 'translate-x-0'
              )}
            >
              <Sidebar />
            </div>
          </SidebarContext.Provider>

          {isMobileOpen ? (
            <button
              type="button"
              className="fixed inset-0 z-30 bg-slate-900/20 backdrop-blur-sm lg:hidden"
              onClick={closeMobile}
              aria-label="Close sidebar"
            />
          ) : null}

          <div className="flex-1 overflow-y-auto lg:ml-[260px]">
            <div className="relative flex min-h-full flex-col gap-6 px-4 pb-10 pt-6 lg:px-8 lg:pt-8">
              <Topbar onToggleSidebar={toggleMobile} />
              <main className="space-y-8">{children}</main>
            </div>
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
}
